// routes/components/PublicRoute.tsx
/**
 * PublicRoute - Wrapper for routes that should redirect if authenticated
 *
 * Routing rules for an already-logged-in user landing on a public
 * page (login page, etc.):
 *  - Player-only accounts go straight to /player.
 *  - Staff accounts (admin / store / staff / approved) get the
 *    post-login picker at /welcome, unless they previously saved a
 *    "remember my choice" preference, in which case we honour that.
 */

import { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PageLoading } from '../../components/common/Loading';
import { ROUTES } from '../../config/routes';
import { readWelcomePreference } from '../../features/auth/pages/WelcomePage';
import type { PublicRouteProps } from '../types';

export function PublicRoute({ children }: PublicRouteProps): ReactElement {
  const { user, loading, isPlayer } = useAuth();

  if (loading) {
    return <PageLoading />;
  }

  if (user) {
    if (isPlayer()) {
      return <Navigate to={ROUTES.PLAYER_DASHBOARD} replace />;
    }
    const remembered = readWelcomePreference();
    if (remembered === 'staff') {
      return <Navigate to={ROUTES.LANDING} replace />;
    }
    if (remembered === 'player') {
      return <Navigate to={ROUTES.PLAYER_DASHBOARD} replace />;
    }
    return <Navigate to={ROUTES.WELCOME} replace />;
  }

  return <>{children}</>;
}

export default PublicRoute;
